import { Check, Copy, Loader2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { anunciosMetaApi, mensagemDeErro } from "./api";

/**
 * Passo a passo da conexão (piloto): a loja compartilha os ativos com o
 * portfólio do Núcleo em Parceiros, digita o ID do portfólio dela e escolhe,
 * entre o que o Núcleo recebeu DESSE portfólio, a conta de anúncio e a Página.
 */
const LINK_CONFIGURACOES = "https://business.facebook.com/settings";
const PASSOS = [
  <>No Facebook, abra as Configurações do negócio do portfólio da loja:{" "}
    <a href={LINK_CONFIGURACOES} target="_blank" rel="noopener noreferrer" className="underline text-orange-800 dark:text-orange-300">business.facebook.com/settings</a></>,
  "Em Usuários → Parceiros, clique em Adicionar → \"Conceder a um parceiro acesso aos seus recursos\"",
  "Cole o ID do Núcleo abaixo e conceda, com controle total: a Página, a conta de anúncio e o Instagram",
  "Copie o ID do portfólio da loja (em Informações da empresa) e cole aqui embaixo",
];

export default function PassoConexao({ nucleoBusinessId, onConectado, onCancelar }) {
  const [businessId, setBusinessId] = useState("");
  const [ativos, setAtivos] = useState(null);
  const [conta, setConta] = useState("");
  const [pagina, setPagina] = useState("");
  const [ocupado, setOcupado] = useState(false);
  const [copiado, setCopiado] = useState(false);

  const copiarId = () => {
    navigator.clipboard?.writeText(nucleoBusinessId);
    setCopiado(true); setTimeout(() => setCopiado(false), 2000);
  };

  const conferir = async () => {
    setOcupado(true);
    try {
      const r = await anunciosMetaApi.ativos(businessId.trim());
      setAtivos(r);
      if (r.contas?.length === 1) setConta(r.contas[0].id);
      if (r.paginas?.length === 1) setPagina(r.paginas[0].id);
      if (!r.compartilhado_com_nucleo) toast.warning("Ainda não encontrei nada desse portfólio compartilhado com o Núcleo.");
    } catch (e) { toast.error(mensagemDeErro(e, "Não foi possível conferir na Meta.")); }
    finally { setOcupado(false); }
  };

  const conectar = async () => {
    const pag = ativos.paginas.find((p) => p.id === pagina);
    setOcupado(true);
    try {
      const r = await anunciosMetaApi.conectar({
        business_id: businessId.trim(), ad_account_id: conta, page_id: pagina, instagram_id: pag?.instagram_id || null,
      });
      toast.success("Loja conectada à Meta.");
      onConectado(r);
    } catch (e) { toast.error(mensagemDeErro(e, "Não foi possível conectar.")); }
    finally { setOcupado(false); }
  };

  return (
    <div className="rounded-xl border bg-card p-5 space-y-5" data-e2e="meta-passo-conexao">
      <div>
        <h2 className="text-lg font-bold">Compartilhe a conta com o Núcleo</h2>
        <p className="text-sm text-muted-foreground mt-1">É o mesmo acesso que se dá a uma agência: a loja continua dona de tudo e pode desfazer quando quiser.</p>
      </div>
      <ol className="space-y-3 text-sm">
        {PASSOS.map((p, i) => (
          <li key={i} className="flex items-start gap-3">
            <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
            <span className="pt-0.5">{p}</span>
          </li>
        ))}
      </ol>
      <div className="rounded-lg bg-muted/40 p-3 space-y-1">
        <p className="text-xs font-semibold">ID do Núcleo (cole em Parceiros)</p>
        <div className="flex items-center gap-2">
          <code className="flex-1 rounded-lg bg-muted px-2.5 py-1.5 text-sm tabular-nums">{nucleoBusinessId}</code>
          <button onClick={copiarId} title="Copiar" className={`rounded-lg border p-2 ${copiado ? "border-emerald-500 text-emerald-500" : "hover:border-primary"}`}>
            {copiado ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>
      <div className="space-y-1">
        <label className="text-xs font-semibold" htmlFor="meta-business-id">ID do portfólio da loja</label>
        <div className="flex gap-2">
          <input id="meta-business-id" data-e2e="meta-business-id" value={businessId} inputMode="numeric"
            onChange={(e) => setBusinessId(e.target.value.replace(/\D/g, ""))} placeholder="Só números"
            className="flex-1 rounded-lg border bg-background px-3 py-2 text-sm" />
          <button onClick={conferir} disabled={ocupado || businessId.length < 5} data-e2e="meta-conferir"
            className="rounded-lg bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-2">
            {ocupado && <Loader2 className="w-4 h-4 animate-spin" />} Conferir conexão
          </button>
        </div>
      </div>
      {ativos && (
        <div className="space-y-3" data-e2e="meta-ativos">
          <Escolha rotulo="Conta de anúncio" vazio="Nenhuma conta de anúncio desse portfólio chegou ao Núcleo ainda."
            opcoes={ativos.contas.map((c) => ({ id: c.id, texto: `${c.nome} · ${c.moeda || "?"} · ${c.pre_paga ? "pré-paga" : "pós-paga"}${c.tem_pagamento ? "" : " · SEM pagamento"}` }))}
            valor={conta} onChange={setConta} />
          <Escolha rotulo="Página do Facebook" vazio="Nenhuma Página desse portfólio chegou ao Núcleo ainda."
            opcoes={ativos.paginas.map((p) => ({ id: p.id, texto: `${p.nome}${p.instagram_username ? ` · @${p.instagram_username}` : ativos.instagram_legivel ? " · sem Instagram ligado" : ""}` }))}
            valor={pagina} onChange={setPagina} />
          <div className="flex gap-2">
            <button onClick={conectar} disabled={ocupado || !conta || !pagina} data-e2e="meta-conectar"
              className="rounded-lg bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold disabled:opacity-50">
              Conectar
            </button>
            <button onClick={onCancelar} className="rounded-lg border px-4 py-2 text-sm">Voltar</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Escolha({ rotulo, opcoes, valor, onChange, vazio }) {
  return (
    <div className="space-y-1.5">
      <p className="text-xs font-semibold">{rotulo}</p>
      {opcoes.length === 0 ? <p className="text-xs text-muted-foreground">{vazio}</p> : opcoes.map((o) => (
        <label key={o.id} className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm cursor-pointer ${valor === o.id ? "border-primary bg-primary/5" : "hover:border-primary/50"}`}>
          <input type="radio" className="accent-primary" checked={valor === o.id} onChange={() => onChange(o.id)} /> {o.texto}
        </label>
      ))}
    </div>
  );
}
